import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Globe2, Landmark, Vote, ScrollText, CheckCircle2, XCircle, MinusCircle, FileDown } from "lucide-react";

const COUNTRY_META = {
  IN: { flag: "🇮🇳", label: "India" },
  US: { flag: "🇺🇸", label: "United States" },
  GB: { flag: "🇬🇧", label: "United Kingdom" },
  AE: { flag: "🇦🇪", label: "United Arab Emirates" },
  SG: { flag: "🇸🇬", label: "Singapore" },
};

/* ─── Live Ecosystem Ticker ────────────────────────────────── */
export function EcosystemTicker({ api }) {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/director/ticker");
        if (alive) setEvents(data.events || []);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [api]);

  if (events.length === 0) return null;
  const feed = [...events, ...events];

  const renderItem = (ev, i) => {
    const inner = (
      <span className="font-body text-sm text-[#F5F5F0] inline-flex items-center gap-2">
        <span className="font-grotesk text-[9px] uppercase tracking-[0.25em] text-[#C9A961]">
          {ev.kind}
        </span>
        <span className="text-[#8A8A82]">·</span>
        {ev.text}
      </span>
    );
    if (ev.href) {
      return (
        <a key={i} href={ev.href} target="_blank" rel="noopener noreferrer"
          className="hover:text-[#C9A961] transition-colors" data-testid={`ticker-link-${i}`}>
          {inner}
        </a>
      );
    }
    return <div key={i}>{inner}</div>;
  };

  return (
    <div className="relative overflow-hidden rounded-sm border border-[#C9A961]/25 bg-gradient-to-r from-[#0a0806] via-[#141008] to-[#0a0806] mb-10" data-testid="director-ticker">
      <div className="absolute left-0 top-0 bottom-0 z-10 w-24 bg-gradient-to-r from-[#0a0806] to-transparent" />
      <div className="absolute right-0 top-0 bottom-0 z-10 w-24 bg-gradient-to-l from-[#0a0806] to-transparent" />
      <div className="flex items-center gap-3 py-3">
        <div className="pl-5 pr-3 flex items-center gap-2 shrink-0 relative z-20">
          <motion.div animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="w-2 h-2 rounded-full bg-[#C9A961]" />
          <span className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#C9A961]">
            Live · Ecosystem
          </span>
        </div>
        <div className="flex-1 overflow-hidden">
          <div className="flex gap-12 whitespace-nowrap animate-[tickerMove_45s_linear_infinite]">
            {feed.map((ev, i) => renderItem(ev, i))}
          </div>
        </div>
      </div>
      <style>{`@keyframes tickerMove { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

/* ─── Board Roster with pie ────────────────────────────────── */
export function BoardRoster({ api }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/director/board").then((r) => setData(r.data)).catch(() => {});
  }, [api]);

  if (!data) return null;

  const size = 260, R = 112, cx = size/2, cy = size/2;
  const palette = ["#C9A961", "#F5D67A", "#8B7333", "#B87333", "#E8C978", "#D8B23E", "#A8863F"];
  let cumulative = 0;
  const slices = data.roster.map((d, i) => {
    const value = d.shareholding_pct;
    const start = cumulative;
    cumulative += value;
    return { ...d, start, end: cumulative, color: palette[i % palette.length] };
  });
  const unallocated = data.unallocated_pct;

  const arc = (start, end) => {
    const a0 = (start/100) * 2*Math.PI - Math.PI/2;
    const a1 = (end/100) * 2*Math.PI - Math.PI/2;
    const large = end - start > 50 ? 1 : 0;
    const x0 = cx + R * Math.cos(a0), y0 = cy + R * Math.sin(a0);
    const x1 = cx + R * Math.cos(a1), y1 = cy + R * Math.sin(a1);
    return `M ${cx} ${cy} L ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1} Z`;
  };

  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-gradient-to-br from-[#0f0d0a] to-[#0a0806] p-8 mb-16" data-testid="director-board-roster">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#C9A961]">· Founders' Board · Live Cap-Table ·</span>
          <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2">Board Roster</h2>
        </div>
        <Landmark className="text-[#C9A961]" size={26} />
      </div>
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div className="flex items-center justify-center">
          <div className="relative" style={{ width: size, height: size }}>
            <motion.div animate={{ scale: [1, 1.04, 1], opacity: [0.3, 0.55, 0.3] }}
              transition={{ duration: 5, repeat: Infinity }}
              className="absolute inset-0 rounded-full"
              style={{ background: "radial-gradient(circle,#C9A961 0%,transparent 70%)", filter: "blur(30px)" }} />
            <svg width={size} height={size} className="relative" style={{ filter: "drop-shadow(0 0 20px rgba(201,169,97,0.4))" }}>
              {slices.map((s, i) => (
                <motion.path
                  key={s.id}
                  d={arc(s.start, s.end)}
                  fill={s.color}
                  stroke="#0a0806"
                  strokeWidth="2"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.08 }}
                />
              ))}
              {unallocated > 0 && (
                <path d={arc(100 - unallocated, 100)} fill="rgba(201,169,97,0.08)" stroke="#0a0806" strokeWidth="2" />
              )}
              <circle cx={cx} cy={cy} r={R * 0.55} fill="#0a0806" />
              <text x={cx} y={cy - 6} textAnchor="middle" fill="#C9A961" fontSize="10" fontFamily="Helvetica" fontWeight="bold" letterSpacing="3">DIRECTORS</text>
              <text x={cx} y={cy + 18} textAnchor="middle" fill="#F5F5F0" fontSize="34" fontFamily="Times-Bold" fontWeight="bold">{data.count}</text>
              <text x={cx} y={cy + 36} textAnchor="middle" fill="#8A8A82" fontSize="9" fontFamily="Helvetica" letterSpacing="2">SEATED</text>
            </svg>
          </div>
        </div>
        <div className="space-y-3">
          {slices.map((d, i) => (
            <motion.div key={d.id}
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.05 }}
              className={`flex items-center gap-4 rounded-sm border p-4 ${d.is_you ? "border-[#C9A961] bg-[#C9A961]/[0.06]" : "border-white/10 bg-black/30"}`}
              data-testid={`board-roster-row-${d.id}`}
            >
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: d.color }} />
              {/* Portrait or initials */}
              <div className="relative w-10 h-10 rounded-full border border-[#C9A961]/40 grid place-items-center bg-[#0a0806] overflow-hidden shrink-0">
                {d.photo_key ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/founders/photo/${d.id}`}
                    alt={d.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="font-serif text-sm text-[#C9A961]">
                    {(d.name || "V").split(" ").map((s) => s[0]).slice(0,2).join("")}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-lg text-[#F5F5F0] truncate">{d.name}</span>
                  <span className="text-lg">{COUNTRY_META[d.country]?.flag || "🏳️"}</span>
                  {d.is_you && (
                    <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9A961] border border-[#C9A961]/60 rounded-full px-2 py-0.5">You</span>
                  )}
                </div>
                <div className="font-body text-xs text-[#8A8A82] truncate">
                  {d.business_name}{d.state_or_region ? ` · ${d.state_or_region}` : ""}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-serif text-xl text-[#F5F5F0] tabular-nums">{d.shareholding_pct.toFixed(2)}%</div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">stake</div>
              </div>
            </motion.div>
          ))}
          {unallocated > 0 && (
            <div className="flex items-center gap-4 rounded-sm border border-white/5 p-3 opacity-70">
              <span className="w-3 h-3 rounded-full bg-white/10 shrink-0" />
              <span className="font-body text-sm text-[#8A8A82] flex-1">Unallocated equity</span>
              <span className="font-serif text-lg text-[#8A8A82] tabular-nums">{unallocated.toFixed(2)}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Board Motions & Voting ───────────────────────────────── */
export function BoardMotionsPanel({ api }) {
  const [motions, setMotions] = useState(null);
  const load = () => api.get("/director/motions").then((r) => setMotions(r.data)).catch(() => setMotions([]));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const vote = async (mid, v) => {
    try {
      await api.post(`/director/motions/${mid}/vote`, { vote: v });
      toast.success(`Vote recorded · ${v.toUpperCase()}`);
      window.dispatchEvent(new Event("director-journey-refresh"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not record vote."); }
  };

  const download = async (mid, seal) => {
    try {
      const r = await api.get(`/director/motions/${mid}/resolution.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `Vinci-Resolution-${seal}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download resolution."); }
  };

  if (!motions) return null;

  return (
    <div className="rounded-sm border border-[#C9A961]/25 bg-gradient-to-br from-[#0f0d0a] to-[#0a0806] p-8 mb-16" data-testid="director-motions">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#C9A961]">· Board Motions · Digital Voting ·</span>
          <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-2">Motions & Resolutions</h2>
        </div>
        <Vote className="text-[#C9A961]" size={26} />
      </div>
      {motions.length === 0 ? (
        <p className="font-body text-[#8A8A82] italic">No motions filed yet. Admin will post Board motions here.</p>
      ) : (
        <div className="space-y-4">
          {motions.map((m) => (
            <div key={m.id} className="rounded-sm border border-[#C9A961]/20 bg-black/30 p-6" data-testid={`motion-${m.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ScrollText size={16} className="text-[#C9A961]" />
                    <h3 className="font-serif text-xl text-[#F5F5F0]">{m.title}</h3>
                    <StatusPill motion={m} />
                  </div>
                  <p className="font-body text-sm text-[#C9C9C2] mt-3 leading-relaxed">{m.description}</p>
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] mt-3">
                    Opened {new Date(m.created_at).toLocaleString()} · Closes {new Date(m.expires_at).toLocaleString()}
                  </div>
                </div>
                {m.status === "closed" && m.resolution_seal && (
                  <button onClick={() => download(m.id, m.resolution_seal)}
                    data-testid={`motion-download-${m.id}`}
                    className="inline-flex items-center gap-2 rounded-sm border border-[#C9A961]/60 text-[#C9A961] px-4 py-2 font-grotesk text-[9px] uppercase tracking-[0.28em] hover:bg-[#C9A961] hover:text-black transition-colors">
                    <FileDown size={14} /> Resolution PDF
                  </button>
                )}
              </div>
              {/* Vote tally */}
              <div className="mt-5 grid grid-cols-3 gap-2">
                <TallyCell kind="for" count={m.vote_counts.counts.for} share={m.vote_counts.shares.for} />
                <TallyCell kind="against" count={m.vote_counts.counts.against} share={m.vote_counts.shares.against} />
                <TallyCell kind="abstain" count={m.vote_counts.counts.abstain} share={m.vote_counts.shares.abstain} />
              </div>
              {/* Vote actions */}
              {m.status === "open" && (
                <div className="mt-5 flex flex-wrap items-center gap-2 pt-4 border-t border-[#C9A961]/10">
                  <span className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82] mr-2">
                    Your vote{m.your_vote ? ` · currently ${m.your_vote.toUpperCase()}` : ""}:
                  </span>
                  {["for", "against", "abstain"].map((v) => (
                    <button key={v} onClick={() => vote(m.id, v)}
                      data-testid={`motion-vote-${v}-${m.id}`}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em] transition-colors ${
                        m.your_vote === v
                          ? v === "for" ? "bg-[#2FB999] text-black"
                            : v === "against" ? "bg-[#C0392B] text-white"
                            : "bg-[#C9A961] text-black"
                          : "border border-white/15 text-[#C9C9C2] hover:border-[#C9A961]"
                      }`}>
                      {v === "for" && <CheckCircle2 size={13} />}
                      {v === "against" && <XCircle size={13} />}
                      {v === "abstain" && <MinusCircle size={13} />}
                      {v}
                    </button>
                  ))}
                </div>
              )}
              {m.status === "closed" && m.outcome && (
                <div className="mt-4 pt-4 border-t border-[#C9A961]/10 font-grotesk text-[10px] uppercase tracking-[0.28em]"
                     style={{ color: m.outcome === "passed" ? "#2FB999" : m.outcome === "rejected" ? "#C0392B" : "#C9A961" }}>
                  Resolution · {m.outcome}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ motion }) {
  const map = {
    open: { c: "#C9A961", l: "OPEN" },
    closed: { c: "#8A8A82", l: "CLOSED" },
  };
  const m = map[motion.status] || map.closed;
  return <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] border rounded-full px-2 py-0.5" style={{ color: m.c, borderColor: `${m.c}66` }}>{m.l}</span>;
}

function TallyCell({ kind, count, share }) {
  const meta = {
    for: { c: "#2FB999", l: "FOR", Icon: CheckCircle2 },
    against: { c: "#C0392B", l: "AGAINST", Icon: XCircle },
    abstain: { c: "#C9A961", l: "ABSTAIN", Icon: MinusCircle },
  }[kind];
  return (
    <div className="rounded-sm border p-3" style={{ borderColor: `${meta.c}40`, background: `${meta.c}08` }}>
      <div className="flex items-center gap-2 font-grotesk text-[9px] uppercase tracking-[0.22em]" style={{ color: meta.c }}>
        <meta.Icon size={12} /> {meta.l}
      </div>
      <div className="font-serif text-xl mt-1 tabular-nums" style={{ color: meta.c }}>{count}</div>
      <div className="font-body text-[10px] text-[#8A8A82] tabular-nums">{share.toFixed(2)}% by shares</div>
    </div>
  );
}

/* ─── Elite Country Flag Badge (for header) ────────────────── */
export function FounderCountryBadge({ country, state }) {
  const meta = COUNTRY_META[country] || { flag: "🏳️", label: country };
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-[#C9A961]/40 bg-[#C9A961]/[0.06] px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#C9A961]"
          data-testid="founder-country-badge">
      <Globe2 size={11} /> <span className="text-base">{meta.flag}</span> {meta.label}
      {state ? ` · ${state}` : ""}
    </span>
  );
}

export { COUNTRY_META };
